import { useCallback, useEffect, useRef, useState } from "react";
import * as P from "./style";
import { isProtectedPostMediaUrl, useProtectedMedia } from "../../hooks/useProtectedMedia";
import {
  activateVideo,
  getSoundOn,
  registerVideo,
  setSoundOn,
  subscribeSoundOn,
  unregisterVideo,
} from "../../Utils/videoPlaybackManager";

interface Props {
  url: string;
  locked: boolean;
  isSubscribed?: boolean;
  posterUrl?: string;
}

interface ManagedVideoOptions {
  sourceReady: boolean;
  mediaError: boolean;
}

const AUTOPLAY_VISIBILITY_THRESHOLD = 0.65;
const PAUSE_VISIBILITY_THRESHOLD = 0.25;

const mediaDebug = (...args: unknown[]) => {
  if (typeof window !== "undefined" && window.localStorage.getItem("fanxoMediaDebug") === "1") {
    console.log("[fanxo-media]", ...args);
  }
};

const VideoUnavailable = () => (
  <P.VideoPlaceholder role="img" aria-label="Media unavailable">Media unavailable</P.VideoPlaceholder>
);

const VideoLoading = ({ posterUrl }: { posterUrl?: string | null }) => (
  <>
    {posterUrl ? <P.PostImage src={posterUrl} alt="" loading="eager" /> : <P.MediaPlaceholder aria-label="Loading video" />}
    <P.VideoLoadingOverlay aria-label="Loading video" />
  </>
);

const useManagedVideo = ({ sourceReady, mediaError }: ManagedVideoOptions) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const autoplayAttemptKeyRef = useRef<string | null>(null);
  const pauseDueToVisibilityRef = useRef(false);
  const userPausedRef = useRef(false);
  const suppressVolumeChangeRef = useRef(false);
  const [videoNode, setVideoNode] = useState<HTMLVideoElement | null>(null);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isVisibleEnough, setIsVisibleEnough] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => getSoundOn());

  useEffect(() => subscribeSoundOn(setSoundEnabled), []);

  useEffect(() => {
    mediaDebug("isPlaying state", isPlaying);
  }, [isPlaying]);

  useEffect(() => {
    mediaDebug("visible enough", isVisibleEnough);
  }, [isVisibleEnough]);

  const setVideoRef = useCallback((node: HTMLVideoElement | null) => {
    if (videoRef.current && videoRef.current !== node) {
      unregisterVideo(videoRef.current);
    }

    videoRef.current = node;
    setVideoNode(node);

    if (node) {
      registerVideo(node);
    }
  }, []);

  const setMutedProgrammatically = useCallback((video: HTMLVideoElement, muted: boolean) => {
    if (video.muted === muted) return;

    suppressVolumeChangeRef.current = true;
    video.muted = muted;
    mediaDebug("muted changes", { muted, programmatic: true });
    window.setTimeout(() => {
      suppressVolumeChangeRef.current = false;
    }, 0);
  }, []);

  const markPlaying = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      activateVideo(video);
    }
    userPausedRef.current = false;
    setIsPlaying(true);
  }, []);

  const markStopped = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const attemptPlay = useCallback(async (reason: "autoplay" | "manual") => {
    const video = videoRef.current;
    if (!video || !sourceReady || mediaError || hasError || !video.paused) return;

    mediaDebug(reason === "autoplay" ? "autoplay attempted" : "click play", {
      readyState: video.readyState,
      soundOn: getSoundOn(),
      muted: video.muted,
    });

    try {
      setMutedProgrammatically(video, reason === "autoplay" ? !getSoundOn() : video.muted);
      await video.play();
      mediaDebug(reason === "autoplay" ? "autoplay success" : "manual play success");
      markPlaying();
      setHasError(false);
    } catch (error) {
      mediaDebug(reason === "autoplay" ? "autoplay failure" : "manual play failure", error);

      if (reason === "autoplay" && getSoundOn() && !video.muted) {
        try {
          setMutedProgrammatically(video, true);
          await video.play();
          mediaDebug("autoplay success", { fallback: "muted" });
          markPlaying();
          setHasError(false);
          return;
        } catch (mutedError) {
          mediaDebug("autoplay failure", { fallback: "muted", error: mutedError });
        }
      }

      setIsPlaying(false);
      if (reason === "manual") {
        setHasError(true);
      }
    }
  }, [hasError, markPlaying, mediaError, setMutedProgrammatically, sourceReady]);

  const handleNativePlay = () => {
    mediaDebug("native play");
    markPlaying();
  };

  const handleNativePlaying = () => {
    mediaDebug("native playing");
    markPlaying();
  };

  const handleNativePause = () => {
    mediaDebug("native pause");
    markStopped();

    if (pauseDueToVisibilityRef.current) {
      pauseDueToVisibilityRef.current = false;
      return;
    }

    userPausedRef.current = true;
  };

  const handleNativeEnded = () => {
    mediaDebug("native ended");
    markStopped();
  };

  const handleLoadedMetadata = () => {
    mediaDebug("loadedmetadata");
    setIsVideoReady(true);
    setHasError(false);
  };

  const handleLoadedData = () => {
    mediaDebug("loadeddata");
    setIsVideoReady(true);
    setHasError(false);
  };

  const handleCanPlay = () => {
    mediaDebug("canplay");
    setIsVideoReady(true);
    setHasError(false);
  };

  const handleError = () => {
    setHasError(true);
  };

  const handleVolumeChange = () => {
    const video = videoRef.current;
    if (!video || suppressVolumeChangeRef.current) return;

    const nextSoundOn = !video.muted && video.volume > 0;
    mediaDebug("muted changes", { muted: video.muted, volume: video.volume, programmatic: false });
    setSoundOn(nextSoundOn);
  };

  useEffect(() => {
    if (!videoNode) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const ratio = entry.intersectionRatio;
        const nextVisibleEnough = entry.isIntersecting && ratio >= AUTOPLAY_VISIBILITY_THRESHOLD;
        setIsVisibleEnough(nextVisibleEnough);

        if (!entry.isIntersecting || ratio < PAUSE_VISIBILITY_THRESHOLD) {
          autoplayAttemptKeyRef.current = null;

          if (!videoNode.paused) {
            mediaDebug("pause due to offscreen");
            pauseDueToVisibilityRef.current = true;
            videoNode.pause();
          }
        }
      },
      { threshold: [0, PAUSE_VISIBILITY_THRESHOLD, AUTOPLAY_VISIBILITY_THRESHOLD, 1] }
    );

    observer.observe(videoNode);

    return () => observer.disconnect();
  }, [videoNode]);

  useEffect(() => {
    setIsVideoReady(false);
    setHasError(false);
    setIsPlaying(false);
    setIsVisibleEnough(false);
    userPausedRef.current = false;
    autoplayAttemptKeyRef.current = null;
  }, [sourceReady]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !sourceReady || mediaError || hasError || !isVisibleEnough || userPausedRef.current) return;
    if (isPlaying || !video.paused) return;

    const attemptKey = `${video.currentSrc || video.src}:${soundEnabled}:${isVideoReady}`;
    if (autoplayAttemptKeyRef.current === attemptKey) return;

    autoplayAttemptKeyRef.current = attemptKey;
    attemptPlay("autoplay");
  }, [attemptPlay, hasError, isPlaying, isVideoReady, isVisibleEnough, mediaError, sourceReady, soundEnabled]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    setMutedProgrammatically(video, !soundEnabled);
  }, [setMutedProgrammatically, soundEnabled, videoNode]);

  return {
    videoRef: setVideoRef,
    hasError,
    setHasError,
    isVideoReady,
    handleNativePlay,
    handleNativePlaying,
    handleNativePause,
    handleNativeEnded,
    handleLoadedMetadata,
    handleLoadedData,
    handleCanPlay,
    handleError,
    handleVolumeChange,
  };
};

const ProtectedPlayer = ({ url, posterUrl }: { url: string; posterUrl?: string }) => {
  const { objectUrl, loading, error: mediaError } = useProtectedMedia(url);
  const { objectUrl: resolvedPosterUrl } = useProtectedMedia(posterUrl);
  const {
    videoRef,
    hasError,
    isVideoReady,
    handleNativePlay,
    handleNativePlaying,
    handleNativePause,
    handleNativeEnded,
    handleLoadedMetadata,
    handleLoadedData,
    handleCanPlay,
    handleError,
    handleVolumeChange,
  } = useManagedVideo({ sourceReady: Boolean(objectUrl), mediaError: Boolean(mediaError) });

  useEffect(() => {
    mediaDebug("previewURL received", posterUrl);
  }, [posterUrl]);

  if (loading) {
    return <VideoLoading posterUrl={resolvedPosterUrl} />;
  }

  if (mediaError || hasError || !objectUrl) {
    return <VideoUnavailable />;
  }

  return (
    <>
      <P.PostVideo
        ref={videoRef}
        src={objectUrl}
        poster={resolvedPosterUrl || undefined}
        controls
        controlsList="nodownload"
        loop
        muted={!getSoundOn()}
        playsInline
        preload="metadata"
        onPlay={handleNativePlay}
        onPlaying={handleNativePlaying}
        onPause={handleNativePause}
        onEnded={handleNativeEnded}
        onLoadedMetadata={handleLoadedMetadata}
        onLoadedData={handleLoadedData}
        onCanPlay={handleCanPlay}
        onError={handleError}
        onVolumeChange={handleVolumeChange}
      />
      {!isVideoReady && <P.VideoLoadingOverlay aria-label="Loading video" />}
    </>
  );
};

const PublicPlayer = ({ url, posterUrl }: { url: string; posterUrl?: string }) => {
  const { objectUrl: resolvedPosterUrl } = useProtectedMedia(posterUrl);
  const {
    videoRef,
    hasError,
    isVideoReady,
    handleNativePlay,
    handleNativePlaying,
    handleNativePause,
    handleNativeEnded,
    handleLoadedMetadata,
    handleLoadedData,
    handleCanPlay,
    handleError,
    handleVolumeChange,
  } = useManagedVideo({ sourceReady: Boolean(url), mediaError: false });

  useEffect(() => {
    mediaDebug("previewURL received", posterUrl);
  }, [posterUrl]);

  if (hasError || !url) {
    return <VideoUnavailable />;
  }

  return (
    <>
      <P.PostVideo
        ref={videoRef}
        src={url}
        poster={resolvedPosterUrl || undefined}
        controls
        controlsList="nodownload"
        preload="metadata"
        loop
        muted={!getSoundOn()}
        playsInline
        onPlay={handleNativePlay}
        onPlaying={handleNativePlaying}
        onPause={handleNativePause}
        onEnded={handleNativeEnded}
        onLoadedMetadata={handleLoadedMetadata}
        onLoadedData={handleLoadedData}
        onCanPlay={handleCanPlay}
        onError={handleError}
        onVolumeChange={handleVolumeChange}
      />
      {!isVideoReady && <P.VideoLoadingOverlay aria-label="Loading video" />}
    </>
  );
};

const VideoPlayer = ({ url, locked, isSubscribed, posterUrl }: Props) => {
  const shouldUseProtectedPlayer = isProtectedPostMediaUrl(url) || (locked && !isSubscribed);

  return shouldUseProtectedPlayer ? (
    <ProtectedPlayer url={url} posterUrl={posterUrl} />
  ) : (
    <PublicPlayer url={url} posterUrl={posterUrl} />
  );
};

export default VideoPlayer;
